import { Button } from "@talladega/components";
import { ArrowUpIcon, LinkExternalIcon } from "@talladega/icons";
import { IAExtendedWarrantyPrograms } from "@modules/purchase/warranty/types/IAProgram";
import useAftermarketPurchaseFromQuery from "@modules/purchase/warranty/hooks/useAftermarketPurchaseFromQuery";
import warrantyComponentsByProgram from "@modules/purchase/warranty/types/WarrantyComponent";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { WarrantyCoverageLayout } from "@modules/cms/types/WarrantyCoverageLayout";
import useCustomerInfoFromQuery from "@modules/query-string/hooks/useCustomerInfoFromQuery";
import useIsBuyerProvinceActiveForAftermarket from "@modules/purchase/warranty/hooks/useIsBuyerProvinceActiveForAftermarket";
import useAftermarketPurchase from "@modules/purchase/warranty/hooks/useAftermarketPurchase";
import { useWarrantyQuotePageContext } from "@modules/purchase/WarrantyQuotePageContext";
import { GetBackgroundStyle, gtmPushData } from "@talladega/helpers";
import { getPrefixUrl } from "@modules/api/api";
import { Event as HotjarEvent, hotjar } from "@modules/core/hotjar/types";
import { tryGetActiveAftermarketPurchase } from "@modules/purchase/warranty/types/Purchase";
import { useState } from "react";
import DealSegment from "@common/enums/DealSegment";
import WarrantyComponent from "./WarrantyComponent";
import {
  vehicleInfoMode,
  WarrantyInstantQuoteModalMode,
} from "../types/WarrantyInstantQuoteModalMode";
import WarrantyInstantQuoteModal from "./WarrantyInstantQuoteModal";
import WarrantyBrochure from "./WarrantyBrochure";

type CtaButtonsProps = {
  source: string;
  onStartPurchase: () => void;
  onOpenCoveragesModal: () => void;
};

const CtaButtons = ({
  source,
  onStartPurchase,
  onOpenCoveragesModal,
}: CtaButtonsProps) => {
  const { t } = useTranslation(["common", "purchase"]);
  const { vin } = useAftermarketPurchaseFromQuery();

  const { province } = useCustomerInfoFromQuery();
  const { isProvinceActive } = useIsBuyerProvinceActiveForAftermarket(
    source,
    province
  );

  return vin && isProvinceActive ? (
    <Button
      rightIcon={<ArrowUpIcon className="h-4" />}
      onClick={onStartPurchase}
    >
      {t("common:get_started")}
    </Button>
  ) : (
    <Button onClick={onOpenCoveragesModal}>{t("purchase:get_a_quote")}</Button>
  );
};

type Props = {
  layout: WarrantyCoverageLayout;
};

const getWarrantySampleContractLink = (
  locale: string,
  provincePostalAbbr?: string,
  programId?: string
) => {
  return `${getPrefixUrl()}/api/purchases/contracts/sample/${provincePostalAbbr}/${programId}?lang=${locale}`;
};

const WarrantyCoverage = ({ layout }: Props) => {
  const { t } = useTranslation(["common", "purchase"]);
  const { purchase: purchaseResponse, isLoading: isPurchaseLoading } =
    useAftermarketPurchase();
  const purchase = tryGetActiveAftermarketPurchase(purchaseResponse);
  const router = useRouter();
  const locale = router.locale || "en";
  const { background } = layout;
  const { province } = useCustomerInfoFromQuery();

  const { setShowCoveragesModal, defaultRates, rangedRates, dealSegment } =
    useWarrantyQuotePageContext();

  const warrantyRates = rangedRates?.data || defaultRates?.data;

  const iaProgram = IAExtendedWarrantyPrograms.find(
    (p) => p.id === warrantyRates?.productDetails?.programId
  );

  const distinctUsedCoverageTypeIds = warrantyRates?.coverages.reduce<string[]>(
    (acc, current) => {
      if (!acc.some((coverage) => coverage === current.type)) {
        acc.push(current.type);
      }
      return acc;
    },
    []
  );

  const [showInstantQuoteModal, setShowInstantQuoteModal] = useState(false);

  const handleOpenCoveragesModal = () => {
    if (!isPurchaseLoading && dealSegment === DealSegment.RangeQuote) {
      setShowInstantQuoteModal(true);
    } else {
      gtmPushData({
        event: "ManualQuoteLookup",
        element: "Get a Quote",
        description: "Get a Quote Button Clicked",
        source: purchase?.dealSource || "",
      });
      setShowCoveragesModal(true);
    }
  };

  const [
    warrantyInstantQuoteModalModeState,
    setWarrantyInstantQuoteModalModeState,
  ] = useState<WarrantyInstantQuoteModalMode>(vehicleInfoMode);

  const handleWarrantySampleContractClick = (
    e: React.MouseEvent<HTMLAnchorElement>
  ) => {
    e.preventDefault();

    const sampleContractUrl = getWarrantySampleContractLink(
      locale,
      province?.abbr,
      iaProgram?.id
    );

    window.open(sampleContractUrl, "_blank");

    gtmPushData({
      event: "Warranty-Sample-Contract",
      element: "View a sample contract",
      description: "Warranty Sample Contract Clicked",
    });

    hotjar.event(HotjarEvent.WarrantySampleContract);
  };

  const triggerScrollToSection = () => {
    const event = new CustomEvent("scrollToWarrantyOptionsSection", {
      detail: {},
    });
    window.dispatchEvent(event);
  };

  const warrantyComponents =
    iaProgram != null && iaProgram.id in warrantyComponentsByProgram
      ? warrantyComponentsByProgram[iaProgram.id]
      : [];

  // filter out components that match 0 returned coverage types
  const filteredWarrantyComponents = warrantyComponents.filter((wc) =>
    wc.coverages.some((c) => distinctUsedCoverageTypeIds?.includes(c))
  );

  if (!defaultRates?.isSuccess && !rangedRates?.isSuccess) {
    return null;
  }

  const baseDomain = process.env.NEXT_PUBLIC_CLOUDFLARE_IMAGES_DOMAIN || "";

  return (
    (filteredWarrantyComponents.length > 0 && (
      <div
        id="cms-warranty-coverage-section"
        className="no-scrollbar relative overflow-visible bg-white bg-cover bg-center"
        style={GetBackgroundStyle(background, baseDomain)}
      >
        <div className="mx-auto max-w-page px-2 pb-6 pt-10 sm:px-4 lg:px-5 lg:py-16">
          <div className="pb-8 text-center">
            <h3 className="text-2xl lg:text-4xl lg:tracking-tight">
              {t("purchase:get_warranty_coverage_fully_online_in_minutes")}
            </h3>
          </div>

          <div className="rounded-lg border border-gray-50 bg-white p-2 pb-6 sm:p-6">
            {warrantyRates && (
              <>
                <div className="overflow-x-auto text-primary">
                  <WarrantyComponent
                    extendedWarrantyDefaultRates={warrantyRates}
                  />
                </div>

                <WarrantyBrochure extWarrantyRates={warrantyRates} />
              </>
            )}

            <div className="mt-8 flex flex-col items-center gap-2">
              <CtaButtons
                onStartPurchase={triggerScrollToSection}
                onOpenCoveragesModal={handleOpenCoveragesModal}
                source={purchase?.dealSource || ""}
              />
              {warrantyRates?.hasSampleContract && (
                <a
                  href="#"
                  rel="noreferrer"
                  onClick={handleWarrantySampleContractClick}
                >
                  <p className="flex items-center gap-x-2 pt-2">
                    <span className="font-medium text-primary-600">
                      {t("purchase:view_sample_contract")}
                    </span>
                    <LinkExternalIcon className="h-6 w-6 text-primary-600" />
                  </p>
                </a>
              )}
            </div>
          </div>
        </div>
        {showInstantQuoteModal && (
          <WarrantyInstantQuoteModal
            showModal={showInstantQuoteModal}
            handleModalClose={() => setShowInstantQuoteModal(false)}
            modeState={[
              warrantyInstantQuoteModalModeState,
              setWarrantyInstantQuoteModalModeState,
            ]}
          />
        )}
      </div>
    )) || <div />
  );
};

export default WarrantyCoverage;
